'use client'

import { useEffect } from 'react'

import { LosiGamesHub } from '@/components/losi-games/losi-games-hub'
import { AsteroidsGame } from '@/components/losi-games/games/asteroids'
import { EyeBreakGame } from '@/components/losi-games/games/eye-break'
import { EyeFlapGame } from '@/components/losi-games/games/eye-flap'
// Match temporarily removed from the arcade — keep import ready to restore.
// import { EyeMatchGame } from '@/components/losi-games/games/eye-match'
import { EyePongGame } from '@/components/losi-games/games/eye-pong'
import { EyeSnakeGame } from '@/components/losi-games/games/eye-snake'
// Pinball temporarily removed from the arcade — keep import ready to restore.
// import { PinballEyesGame } from '@/components/losi-games/games/pinball-eyes'
import { SpaceGame } from '@/components/losi-games/games/space'
// Vibecode temporarily removed from the arcade — keep import ready to restore.
// import { VibecodeStudio } from '@/components/losi-games/games/vibecode-studio'
import type { LosiGameId, LosiGamesOpenOrigin, LosiGamesPhase } from '@/lib/losi-games/types'

const MORPH_MS = 820

interface LosiGamesOverlayProps {
  phase: LosiGamesPhase
  origin: LosiGamesOpenOrigin
  activeGame: LosiGameId | null
  onExpanded: () => void
  onClose: () => void
  onPlay: (id: LosiGameId) => void
  onBack: () => void
}

function reducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function LosiGamesOverlay({
  phase,
  origin,
  activeGame,
  onExpanded,
  onClose,
  onPlay,
  onBack
}: LosiGamesOverlayProps) {
  useEffect(() => {
    if (phase !== 'expanding') return
    const ms = reducedMotion() ? 0 : MORPH_MS
    const t = window.setTimeout(onExpanded, ms)
    return () => window.clearTimeout(t)
  }, [phase, onExpanded])

  useEffect(() => {
    if (phase === 'closed') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (phase === 'playing') onBack()
        else onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [phase, onClose, onBack])

  if (phase === 'closed') return null

  const expanding = phase === 'expanding'
  const cx = origin.cx ?? origin.x * (typeof window !== 'undefined' ? window.innerWidth : 0)
  const cy = origin.cy ?? origin.y * (typeof window !== 'undefined' ? window.innerHeight : 0)
  const w = origin.w ?? 48
  const h = origin.h ?? 48
  const blob = Math.max(w, h) * 0.92

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col text-white"
      role="dialog"
      aria-modal="true"
      aria-label="Losi Arcade"
      style={{ background: expanding ? 'transparent' : '#000' }}
    >
      {expanding ? (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div
            className="absolute rounded-full bg-black"
            style={{
              left: cx,
              top: cy,
              width: blob,
              height: blob,
              transform: 'translate(-50%, -50%)',
              animation: `losi-games-blob ${MORPH_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`
            }}
          />
          {origin.url ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={origin.url}
              alt=""
              aria-hidden
              className="absolute object-contain"
              style={{
                left: cx,
                top: cy,
                width: w,
                height: h,
                transform: 'translate(-50%, -50%)',
                animation: `losi-games-zoom ${MORPH_MS}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`
              }}
            />
          ) : null}
          <style>{`
            @keyframes losi-games-blob {
              0% {
                width: ${blob}px;
                height: ${blob}px;
                border-radius: 42%;
                opacity: 0.92;
              }
              55% {
                width: 120vmax;
                height: 120vmax;
                border-radius: 36%;
                opacity: 1;
              }
              100% {
                width: 180vmax;
                height: 180vmax;
                border-radius: 0;
                opacity: 1;
              }
            }
            @keyframes losi-games-zoom {
              0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
              70% { transform: translate(-50%, -50%) scale(2.4); opacity: 0.55; }
              100% { transform: translate(-50%, -50%) scale(3.2); opacity: 0; }
            }
            @media (prefers-reduced-motion: reduce) {
              .absolute { animation: none !important; }
            }
          `}</style>
        </div>
      ) : null}

      {(phase === 'hub' || phase === 'playing') && (
        <div className="relative z-10 flex h-full min-h-0 flex-col bg-black">
          <header className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <div className="flex items-center gap-3">
              {phase === 'playing' ? (
                <button
                  type="button"
                  onClick={onBack}
                  className="rounded-md px-2 py-1 text-sm text-white/70 hover:bg-white/10 hover:text-white"
                >
                  ← Hub
                </button>
              ) : (
                <span className="text-sm font-medium tracking-wide text-white/90">Losi Arcade</span>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-2 py-1 text-sm text-white/60 hover:bg-white/10 hover:text-white"
            >
              Close
            </button>
          </header>

          <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden px-3 sm:px-4 md:px-5">
            {phase === 'hub' ? <LosiGamesHub onPlay={onPlay} /> : null}
            {phase === 'playing' && activeGame === 'ping-pong' ? <EyePongGame /> : null}
            {phase === 'playing' && activeGame === 'eye-break' ? <EyeBreakGame /> : null}
            {phase === 'playing' && activeGame === 'eye-snake' ? <EyeSnakeGame /> : null}
            {phase === 'playing' && activeGame === 'eye-flap' ? <EyeFlapGame /> : null}
            {/* Match temporarily removed — restore with EyeMatchGame when it returns.
            {phase === 'playing' && activeGame === 'eye-match' ? <EyeMatchGame /> : null} */}
            {phase === 'playing' && activeGame === 'space-shooter' ? <SpaceGame /> : null}
            {phase === 'playing' && activeGame === 'asteroids' ? <AsteroidsGame /> : null}
            {/* Pinball temporarily removed — restore with PinballEyesGame when it returns.
            {phase === 'playing' && activeGame === 'pinball' ? <PinballEyesGame /> : null} */}
            {/* Vibecode temporarily removed — restore with VibecodeStudio when it returns.
            {phase === 'playing' && activeGame === 'vibecode' ? <VibecodeStudio /> : null} */}
          </div>
        </div>
      )}
    </div>
  )
}
